import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { MagnifyingGlassIcon, FunnelIcon, AcademicCapIcon, UserGroupIcon, SparklesIcon, CheckBadgeIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon, CheckBadgeIcon as CheckBadgeSolidIcon } from '@heroicons/react/24/solid'

interface Teacher {
  id: string
  title: string
  introduction: string
  specialties: string[]
  rating: number
  status: string
  bio: string
  experience: string
  education: string
  user: {
    firstName: string
    lastName: string
    avatar?: string
  }
  _count?: {
    courses: number
  }
}

const TeachersPage = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSpecialty, setSelectedSpecialty] = useState('')
  const [sortBy, setSortBy] = useState('rating')
  const [showFilters, setShowFilters] = useState(false)

  const specialties = [
    'JavaScript', 'React', 'Vue', 'Node.js', '初中数学', '高中数学', '数学竞赛',
    '英语口语', '发音纠正', '商务英语', '雅思', '托福'
  ]

  useEffect(() => {
    fetchTeachers()
  }, [])

  useEffect(() => {
    filterAndSortTeachers()
  }, [teachers, searchTerm, selectedSpecialty, sortBy])

  const fetchTeachers = async () => {
    try {
      const response = await fetch('/api/teachers')
      if (response.ok) {
        const data = await response.json()
        // API 返回 { teachers: [...], total, page } 格式
        // 需要提取 teachers 数组，同时兼容直接返回数组的情况
        const teachersList = Array.isArray(data) ? data : (data.teachers || [])
        setTeachers(teachersList)
      }
    } catch (error) {
      console.error('获取教师列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterAndSortTeachers = () => {
    let filtered = teachers.filter(teacher => {
      const matchesSearch = 
        teacher.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.introduction.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesSpecialty = !selectedSpecialty || 
        teacher.specialties.some(specialty => specialty.includes(selectedSpecialty))
      
      const isActive = teacher.status === 'active'

      return matchesSearch && matchesSpecialty && isActive
    })

    // 排序
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating
        case 'experience':
          return b.experience.localeCompare(a.experience)
        case 'courses':
          return (b._count?.courses || 0) - (a._count?.courses || 0)
        default:
          return `${a.user.firstName} ${a.user.lastName}`.localeCompare(`${b.user.firstName} ${b.user.lastName}`)
      }
    })

    setFilteredTeachers(filtered)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedSpecialty('')
    setSortBy('rating')
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="h-10 w-48 bg-gray-200 rounded-lg animate-pulse mb-3"></div>
          <div className="h-5 w-72 bg-gray-100 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gray-200 rounded-2xl"></div>
                <div className="flex-1">
                  <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 w-32 bg-gray-100 rounded"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-gray-100 rounded"></div>
                <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:px-0">
        {/* 页面标题 - 高级版 */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <UserGroupIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">优秀导师</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl">
            汇聚全球顶尖学府导师，为您提供专业的一对一辅导服务
          </p>
          
          {/* 统计信息 */}
          <div className="mt-6 flex flex-wrap gap-4 sm:gap-6">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CheckBadgeSolidIcon className="w-5 h-5 text-green-500" />
              <span><strong className="text-gray-900">{teachers.length}</strong> 位认证导师</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <StarSolidIcon className="w-5 h-5 text-amber-400" />
              <span>平均评分 <strong className="text-gray-900">4.9</strong></span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <SparklesIcon className="w-5 h-5 text-blue-500" />
              <span>100% 真人认证</span>
            </div>
          </div>
        </div>

        {/* 搜索和筛选区域 - 高级版 */}
        <div className="bg-white/80 backdrop-blur-xl shadow-xl rounded-2xl border border-gray-100 p-6 sm:p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* 搜索框 */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="搜索教师..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* 筛选按钮 */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="h-3 w-3 mr-2" />
                筛选
              </button>

              {/* 排序 */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="rating">按评分排序</option>
                <option value="experience">按经验排序</option>
                <option value="courses">按课程数量排序</option>
                <option value="name">按姓名排序</option>
              </select>
            </div>
          </div>

          {/* 筛选选项 */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 专长筛选 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    专业领域
                  </label>
                  <select
                    value={selectedSpecialty}
                    onChange={(e) => setSelectedSpecialty(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">所有专业</option>
                    {specialties.map(specialty => (
                      <option key={specialty} value={specialty}>{specialty}</option>
                    ))}
                  </select>
                </div>

                {/* 清除筛选 */}
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    清除筛选
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 结果统计 */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            找到 <span className="font-semibold text-gray-900">{filteredTeachers.length}</span> 位优秀导师
          </p>
        </div>

        {/* 教师列表 - 高级版 */}
        {filteredTeachers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeachers.map((teacher, index) => (
              <div 
                key={teacher.id} 
                className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:border-purple-200 hover:-translate-y-1 transition-all duration-500"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="p-6">
                  {/* 教师头像和基本信息 */}
                  <div className="flex items-start gap-4 mb-5">
                    <div className="relative">
                      <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center overflow-hidden ring-2 ring-white shadow-lg group-hover:scale-105 transition-transform">
                        {teacher.user.avatar ? (
                          <img
                            className="h-16 w-16 rounded-2xl object-cover"
                            src={teacher.user.avatar}
                            alt={`${teacher.user.firstName} ${teacher.user.lastName}`}
                          />
                        ) : (
                          <span className="text-xl font-bold text-purple-600">
                            {teacher.user.firstName.charAt(0)}{teacher.user.lastName.charAt(0)}
                          </span>
                        )}
                      </div>
                      {/* 认证徽章 */}
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                        <CheckBadgeSolidIcon className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-purple-600 transition-colors truncate">
                        {teacher.user.firstName} {teacher.user.lastName}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{teacher.title}</p>
                      {/* 评分 */}
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <div className="flex items-center">
                          {[0, 1, 2, 3, 4].map((rating) => (
                            <StarSolidIcon
                              key={rating}
                              className={`${
                                (parseFloat(String(teacher.rating)) || 0) > rating ? 'text-amber-400' : 'text-gray-200'
                              } h-4 w-4 flex-shrink-0`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {(parseFloat(String(teacher.rating)) || 0).toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({teacher._count?.courses || 0} 门课)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 简介 */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                    {teacher.introduction}
                  </p>

                  {/* 专长标签 */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1.5">
                      {teacher.specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-50 to-indigo-50 text-purple-700 border border-purple-100"
                        >
                          {specialty}
                        </span>
                      ))}
                      {teacher.specialties.length > 3 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 text-gray-600">
                          +{teacher.specialties.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 经验和教育背景 */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-5 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-1">
                      <AcademicCapIcon className="w-4 h-4" />
                      <span>{teacher.education}</span>
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex gap-3">
                    <Link
                      to={`/teachers/${teacher.id}`}
                      className="flex-1 text-center px-4 py-2.5 border-2 border-gray-200 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 hover:border-purple-200 transition-all"
                    >
                      查看详情
                    </Link>
                    <Link
                      to={`/teachers/${teacher.id}/profile`}
                      className="flex-1 text-center px-4 py-2.5 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                    >
                      预约课程
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
              <UserGroupIcon className="w-10 h-10 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">没有找到符合条件的导师</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">尝试调整搜索条件或清除筛选来查看更多优秀导师</p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-lg hover:shadow-purple-500/25 transition-all"
            >
              <SparklesIcon className="w-4 h-4 mr-2" />
              清除筛选条件
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default TeachersPage 